import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import Modal from "@/components/Modal";
import {
  createActivity as UploadActivityAPI,
  editActivity as UpdateActivityAPI,
} from "@/services/api/activity";
import type { ActivityFormPayload, ActivityInfo } from "@/services/api/activity/types";
import { emptyActivityForm, toActivityFormPayload } from "@/utils/activityFormHydrate";
import ActivityFormInfoStep from "./ActivityFormInfoStep";
import ActivityFormDateStep from "./ActivityFormDateStep";

const STEP_COUNT = 2;

const STEP_KEYS = [
  "Title*",
  "Date & Time*",
] as const;

interface ActivityFormWizardProps {
  mode: "create" | "edit";
  activity?: ActivityInfo | null;
  /** List base the wizard returns to, e.g. `/portal/activities`. */
  basePath?: string;
  /**
   * Business sessions whose subscription ended (`access_status === "locked"`).
   * Set by the business portal page only — the student pages never pass it,
   * so their submit stays enabled.
   */
  subscriptionLocked?: boolean;
}

const ActivityFormWizard: React.FC<ActivityFormWizardProps> = ({
  mode,
  activity,
  basePath = "/student/activities",
  subscriptionLocked = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Only a business account may price an activity. Student and organizer
  // activities are free; the server enforces the same rule.
  const isBusinessAccount = useSelector(
    (state: any) => state.auth?.user_auth === "business" || state.auth?.user?.user_auth === "business",
  );

  const [payload, setPayload] = useState<ActivityFormPayload>(emptyActivityForm());
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [successText, setSuccessText] = useState("");
  const [pendingActivityId, setPendingActivityId] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "edit" && activity) {
      setPayload(toActivityFormPayload(activity));
    }
  }, [mode, activity]);

  const update = useCallback(
    <K extends keyof ActivityFormPayload>(key: K, value: ActivityFormPayload[K]) => {
      setPayload((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const validateStep = (target: number): boolean => {
    if (target === 1) {
      if (!payload.title.trim()) {
        setAlertText(t("Please Input Event Title"));
        return false;
      }
    }

    if (target === 2) {
      if (!payload.date) {
        setAlertText(t("Please Select Event Start Date"));
        return false;
      }
    }

    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, STEP_COUNT));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (step === 1) {
      navigate(-1);
      return;
    }
    setStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (submitting || subscriptionLocked) return;
    if (!validateStep(1) || !validateStep(2)) return;

    const cleanPayload: ActivityFormPayload = {
      ...payload,
      title: payload.title.trim(),
      fee: isBusinessAccount ? payload.fee : 0,
    };

    setSubmitting(true);
    const activityId = activity?.id;
    const resp =
      mode === "create" || !activityId
        ? await UploadActivityAPI(cleanPayload)
        : await UpdateActivityAPI(activityId, cleanPayload);
    setSubmitting(false);

    if (resp?.status !== 0) {
      if (resp?.status === -40032) {
        // The server refused with the shared subscription-ended text; `t`
        // translates it where a resource exists and returns it as-is otherwise.
        const serverMessage = (
          resp.result as { message?: string } | undefined
        )?.message;
        setAlertText(
          t(
            serverMessage ||
              "Your subscription has ended. Please renew to continue.",
          ),
        );
        return;
      }
      setAlertText(t(mode === "create" ? "Can`t Upload Event" : "Can`t Edit Event"));
      return;
    }

    setSuccessText(
      t(mode === "create" ? "Create Event Successfully!" : "Edit Event Successfully!"),
    );
    const created = resp.result as ActivityInfo[] | ActivityInfo | undefined;
    const createdId = Array.isArray(created) ? created[0]?.id : created?.id;
    setPendingActivityId(createdId ?? activityId ?? null);
  };

  const closeSuccess = () => {
    const target = pendingActivityId ?? activity?.id;
    setSuccessText("");
    setPendingActivityId(null);
    if (target) navigate(`${basePath}/${target}`, { replace: true });
    else navigate(basePath, { replace: true });
  };

  const steps = STEP_KEYS.map((key, index) => {
    const number = index + 1;
    const state = number < step ? "done" : number === step ? "active" : "todo";
    return (
      <li key={key} className="flex flex-1 items-center gap-2">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold transition ${
            state === "todo"
              ? "bg-gray-100 text-gray-400"
              : "bg-[#ffa217] text-white"
          }`}
        >
          {state === "done" ? <Check size={14} /> : number}
        </span>
        <span
          className={`hidden truncate text-[12px] font-semibold sm:block ${
            state === "active" ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {t(key)}
        </span>
      </li>
    );
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <ol className="flex items-center gap-2">{steps}</ol>
      </div>

      {step === 1 && (
        <ActivityFormInfoStep
          payload={payload}
          onChange={update}
          showFee={isBusinessAccount}
        />
      )}
      {step === 2 && (
        <ActivityFormDateStep payload={payload} onChange={update} />
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
        >
          {t("BACK")}
        </button>
        {step < STEP_COUNT ? (
          <button
            type="button"
            onClick={goNext}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#e08f0e]"
          >
            {t("NEXT")}
          </button>
        ) : (
          <div className="flex flex-col items-end gap-2">
            {subscriptionLocked && (
              <p className="max-w-xs rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-right text-xs font-semibold text-amber-700">
                {t("Your subscription has ended. Please renew to continue.")}
              </p>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || subscriptionLocked}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#e08f0e] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {t(mode === "create" ? "UPLOAD EVENT" : "EDIT EVENT")}
            </button>
          </div>
        )}
      </div>

      <Modal visible={Boolean(alertText)} onClose={() => setAlertText("")}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <p className="mb-4 text-sm font-semibold text-gray-900">{alertText}</p>
          <button
            type="button"
            onClick={() => setAlertText("")}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>

      <Modal visible={Boolean(successText)} onClose={closeSuccess}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <p className="mb-4 text-sm font-semibold text-gray-900">{successText}</p>
          <button
            type="button"
            onClick={closeSuccess}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ActivityFormWizard;
